/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps, import/no-anonymous-default-export, no-useless-escape */
import React,{useState,useEffect,useCallback} from 'react';
import Layout from '../components/layout/Layout';
import StatCard from '../components/common/StatCard';
import SlidePanel from '../components/common/SlidePanel';
import DataTable from '../components/common/DataTable';
import Badge from '../components/common/Badge';
import {Input,Select} from '../components/common/FormField';
import api from '../services/api';
import {fmtDate,fmtDateTime} from '../utils/helpers';
import {getCurrencySymbol} from '../utils/locale';
import {useTranslation} from 'react-i18next';

function mfilter<T>(items: T[], query: string, fields: string[]): T[] {
  if (!query) return items;
  const q = query.toLowerCase();
  return items.filter((item:any) =>
    fields.some(f => {
      const val = f.split('.').reduce((o: any, k) => o?.[k], item);
      return val != null && String(val).toLowerCase().includes(q);
    })
  );
}


const CATEGORIES=['Medicines','Surgical Consumables','Laboratory Reagents','Medical Supplies','Stationery','Housekeeping','Equipment Spares','Other'];
const ITEM_TYPES=['CONSUMABLE','MEDICINE','REAGENT','EQUIPMENT','STATIONERY','HOUSEKEEPING'];
const DEPARTMENTS=['Pharmacy','Laboratory','OT','IPD','Emergency','Radiology','General'];
const PR_PRIORITIES=['ROUTINE','URGENT','EMERGENCY'];
const ADJUST_REASONS=['PHYSICAL_COUNT','DAMAGE','EXPIRED','THEFT','CORRECTION'];

export default function InventoryPage(){
  const {t}=useTranslation();
  const [page,setPage]=useState(1);
  const PAGE_SIZE=20;
  const [tab,setTab]=useState<'items'|'stock'|'pr'|'po'|'grn'|'issues'|'transfers'|'expiry'|'reports'>('items');
  const [stats,setStats]=useState<any>({});
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState('');
  const [saving,setSaving]=useState(false);

  // Items
  const [items,setItems]=useState<any[]>([]);
  const [itemTotal,setItemTotal]=useState(0);
  const [itemSearch,setItemSearch]=useState('');
  const [itemCatFilter,setItemCatFilter]=useState('');
  const [showItemForm,setShowItemForm]=useState(false);
  const [editItemId,setEditItemId]=useState<string|null>(null);
  const [itemForm,setItemForm]=useState<any>({itemName:'',itemCode:'',category:'',subCategory:'',itemType:'CONSUMABLE',unit:'PCS',hsnCode:'',gstPercentage:'12',reorderLevel:'5',reorderQuantity:'10',unitCost:'',rackLocation:'',
      manufactureDate:'',
      reservedQuantity:'0',
      availableQuantity:'0',
      adjustmentNumber:'',
      drugId:'',
      batchId:'',
      drugName:'',
      quantityBefore:'',
      quantityAfter:'',
      adjustedBy:'',
      fromDepartment:'',
      supplierId:'',
      receivedDate:'',
      supplierName:'',
      supplierGstin:'',
      supplierContact:''
    });

  // Warehouses
  const [warehouses,setWarehouses]=useState<any[]>([]);

  // Stock
  const [stock,setStock]=useState<any[]>([]);

  // PR
  const [prs,setPRs]=useState<any[]>([]);
  const [showPRForm,setShowPRForm]=useState(false);
  const [prForm,setPRForm]=useState<any>({department:'Pharmacy',priority:'ROUTINE',expectedDate:'',remarks:''});
  const [prItems,setPRItems]=useState<any[]>([{itemId:'',itemName:'',quantityRequested:'',unit:'PCS',lastPrice:''}]);

  // PO
  const [pos,setPOs]=useState<any[]>([]);
  const [showPOForm,setShowPOForm]=useState(false);
  const [vendors,setVendors]=useState<any[]>([]);
  const [poForm,setPoForm]=useState<any>({vendorId:'',vendorName:'',expectedDeliveryDate:'',notes:''});
  const [poItems,setPoItems]=useState<any[]>([{itemId:'',itemName:'',quantity:'',unitPrice:'',taxPercentage:'12',unit:'PCS'}]);

  // GRN
  const [grns,setGRNs]=useState<any[]>([]);
  const [showGRNForm,setShowGRNForm]=useState(false);
  const [grnForm,setGrnForm]=useState<any>({poNumber:'',vendorInvoiceNumber:'',vendorInvoiceDate:'',warehouseId:'',remarks:''});
  const [grnItems,setGrnItems]=useState<any[]>([{itemId:'',itemName:'',batchNumber:'',expiryDate:'',quantityReceived:'',purchaseRate:'',saleRate:''}]);

  // Issues
  const [issues,setIssues]=useState<any[]>([]);
  const [showIssueForm,setShowIssueForm]=useState(false);
  const [issueForm,setIssueForm]=useState<any>({fromWarehouseId:'',toDepartment:'Pharmacy',remarks:''});
  const [issueItems,setIssueItems]=useState<any[]>([{itemId:'',itemName:'',batchNumber:'',quantityRequested:'',quantityIssued:'',unitCost:''}]);

  // Expiry
  const [expiring,setExpiring]=useState<any[]>([]);
  const [expiryDays,setExpiryDays]=useState(90);

  // Reports
  const [reportData,setReportData]=useState<any>(null);
  const [reportDates,setRD]=useState<any>({from:new Date(Date.now()-30*86400000).toISOString().split('T')[0],to:new Date().toISOString().split('T')[0]});
  const [valuationData,setValuationData]=useState<any[]>([]);

  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  useEffect(()=>{
    api.getInventoryStats().then(r=>setStats(r.data||{})).catch(()=>{});
    api.getWarehouses().then(r=>setWarehouses(r.data||[])).catch(()=>{});
    api.getVendors().then(r=>setVendors(r.data||[])).catch(()=>{});
  },[]);

  const loadTab=useCallback(async()=>{
    setLoading(true);
    try{
      if(tab==='items'){const r=await api.getInventoryItems({search:itemSearch,category:itemCatFilter,take:100});setItems(r.data.items||[]);setItemTotal(r.data.total||0);}
      if(tab==='stock'){const r=await api.getStockSummary();setStock(r.data||[]);}
      if(tab==='pr'){const r=await api.getInventoryPRs();setPRs(r.data||[]);}
      if(tab==='po'){const r=await api.getInventoryPOs();setPOs(r.data||[]);}
      if(tab==='grn'){const r=await api.getInventoryGRNs();setGRNs(r.data||[]);}
      if(tab==='issues'){const r=await api.getDepartmentIssues();setIssues(r.data.data||[]);}
      if(tab==='expiry'){const r=await api.getExpiringInventory(expiryDays);setExpiring(r.data||[]);}
      if(tab==='reports'){
        const [c,v]=await Promise.all([api.getDeptConsumptionReport(reportDates),api.getInventoryValuation()]);
        setReportData(c.data||[]);setValuationData(v.data||[]);
      }
    }catch{}finally{setLoading(false);}
  },[tab,itemSearch,itemCatFilter,expiryDays,reportDates]);
  useEffect(()=>{const t=setTimeout(loadTab,300);return()=>clearTimeout(t);},[loadTab]);

  // ── Item CRUD ──
  const saveItem=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{
      if(editItemId)await api.updateInventoryItem(editItemId,itemForm);
      else await api.createInventoryItem(itemForm);
      notify('✅ Item saved!');setShowItemForm(false);setEditItemId(null);loadTab();
    }catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}finally{setSaving(false);}
  };
  const openEditItem=(item:any)=>{
    setItemForm({itemName:item.item_name||item.itemName,itemCode:item.item_code||item.itemCode||'',category:item.category||'',subCategory:item.subCategory||'',itemType:item.itemType||'CONSUMABLE',unit:item.unit||'PCS',hsnCode:item.hsnCode||'',gstPercentage:item.gstPercentage||12,reorderLevel:item.reorderLevel||5,reorderQuantity:item.reorderQuantity||10,unitCost:item.unitCost||'',rackLocation:item.rackLocation||''});
    setEditItemId(item.id);setShowItemForm(true);
  };

  // ── PR ──
  const submitPR=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{await api.createInventoryPR({...prForm,items:prItems.filter(i=>i.itemName&&i.quantityRequested)});notify('✅ PR created!');setShowPRForm(false);setPRForm({department:'Pharmacy',priority:'ROUTINE',expectedDate:'',remarks:''});setPRItems([{itemId:'',itemName:'',quantityRequested:'',unit:'PCS',lastPrice:''}]);loadTab();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}finally{setSaving(false);}
  };

  const approvePR=async(id:string,approve:boolean)=>{
    await api.updateInventoryPRStatus(id,{status:approve?'APPROVED':'REJECTED'});notify(approve?'✅ PR Approved':'PR Rejected');loadTab();
  };

  // ── PO ──
  const submitPO=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    const vendor=vendors.find((v:any)=>v.id===poForm.vendorId);
    try{await api.createInventoryPO({...poForm,vendorName:vendor?.vendorName||poForm.vendorName,items:poItems.filter(i=>i.itemName&&i.quantity)});notify('✅ PO created!');setShowPOForm(false);loadTab();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}finally{setSaving(false);}
  };

  // ── GRN ──
  const submitGRN=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{await api.createInventoryGRN({...grnForm,items:grnItems.filter(i=>i.itemName&&i.quantityReceived)});notify('✅ GRN created!');setShowGRNForm(false);loadTab();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}finally{setSaving(false);}
  };
  const postGRN=async(id:string)=>{
    setSaving(true);try{await api.postInventoryGRN(id);notify('✅ GRN Posted — stock updated!');loadTab();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}finally{setSaving(false);}
  };

  // ── Issue ──
  const submitIssue=async(e:React.FormEvent)=>{
    e.preventDefault();setSaving(true);
    try{await api.createDepartmentIssue({...issueForm,items:issueItems.filter(i=>i.itemName&&i.quantityIssued)});notify('✅ Items issued!');setShowIssueForm(false);loadTab();}
    catch(e:any){notify('❌ '+(e.response?.data?.message||'Error'));}finally{setSaving(false);}
  };

  const ifi=(k:string)=>({value:(itemForm as any)[k],onChange:(e:any)=>setItemForm({...itemForm,[k]:e.target.value})});

  const TABS=[['items','📦 Item Master'],['stock','📊 Stock'],['pr','📋 Requisitions'],['po','🛒 Purchase Orders'],['grn','📥 GRN'],['issues','🏥 Issue to Dept'],['expiry','⏰ Expiry'],['reports','📈 Reports']];

  return(
    <Layout title="Inventory / Store Management" actions={
      <div className="flex gap-2">
        {tab==='items'&&<button onClick={()=>{setItemForm({itemName:'',itemCode:'',category:'',subCategory:'',itemType:'CONSUMABLE',unit:'PCS',hsnCode:'',gstPercentage:'12',reorderLevel:'5',reorderQuantity:'10',unitCost:'',rackLocation:''});setEditItemId(null);setShowItemForm(true);}} className="btn-primary text-sm">➕ Add Item</button>}
        {tab==='pr'&&<button onClick={()=>setShowPRForm(true)} className="btn-primary text-sm">➕ New PR</button>}
        {tab==='po'&&<button onClick={()=>setShowPOForm(true)} className="btn-primary text-sm">➕ New PO</button>}
        {tab==='grn'&&<button onClick={()=>setShowGRNForm(true)} className="btn-primary text-sm">➕ New GRN</button>}
        {tab==='issues'&&<button onClick={()=>setShowIssueForm(true)} className="btn-primary text-sm">➕ Issue Items</button>}
      </div>
    }>
      {msg&&<div className={`mb-4 p-3 rounded-lg text-sm border ${msg.startsWith('✅')?'bg-green-50 text-green-700 border-green-200':msg.startsWith('⚠️')?'bg-amber-50 text-amber-700 border-amber-200':'bg-red-50 text-red-700 border-red-200'}`}>{msg}</div>}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
        <StatCard label="Total Items" value={stats.totalItems||0} icon="📦" color="blue"/>
        <StatCard label="Low Stock" value={stats.lowStockCount||0} icon="⚠️" color="amber"/>
        <StatCard label="Out of Stock" value={stats.outOfStockCount||0} icon="🚫" color="red"/>
        <StatCard label="Pending POs" value={stats.pendingPOs||0} icon="🛒" color="purple"/>
        <StatCard label="Expiring <90d" value={stats.expiringCount||0} icon="⏰" color="red"/>
        <StatCard label="Stock Value" value={`${getCurrencySymbol()}${(stats.totalInventoryValue||0).toLocaleString('en-KE')}`} icon="💰" color="green"/>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 overflow-x-auto pb-1 border-b">
        {TABS.map(([t,l])=>(
          <button key={t} onClick={()=>setTab(t as any)}
            className={`px-3 py-2 rounded-t-lg text-xs font-medium whitespace-nowrap flex-shrink-0 transition-all ${tab===t?'bg-[#02357A] text-white':'text-slate-600 hover:bg-slate-100'}`}>{l}</button>
        ))}
      </div>

      {/* ITEMS */}
      {tab==='items'&&(
        <div>
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <div className="relative flex-1 min-w-44"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span><input className="input pl-9" placeholder="Search item name or code..." value={itemSearch} onChange={e=>setItemSearch(e.target.value)}/></div>
            <Select value={itemCatFilter} onChange={e=>setItemCatFilter(e.target.value)} className="!w-44"><option value="">All Categories</option>{CATEGORIES.map(c=><option key={c}>{c}</option>)}</Select>
            <span className="text-sm text-slate-500">{itemTotal} items</span>
          </div>
          <div className="card overflow-hidden">
            <DataTable loading={loading} emptyMessage="No items in master" emptyIcon="📦" data={items} columns={[
              {key:'code',label:'Code',render:(r:any)=><span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{r.item_code||r.itemCode||'—'}</span>},
              {key:'name',label:'Item Name',render:(r:any)=><div><p className="font-medium">{r.item_name||r.itemName}</p><p className="text-xs text-slate-400">{r.category}{r.subCategory?` · ${r.subCategory}`:''}</p></div>},
              {key:'type',label:'Type',render:(r:any)=><Badge label={r.itemType||'CONSUMABLE'} className="bg-blue-50 text-[#02357A]"/>},
              {key:'unit',label:'Unit',render:(r:any)=><span className="text-slate-500 text-xs">{r.unit||r.uom||'—'}</span>},
              {key:'stock',label:'Stock',render:(r:any)=><span className={`font-bold ${+r.currentStock<=(+r.reorderLevel||5)?'text-red-600':+r.currentStock===0?'text-red-700':'text-green-600'}`}>{r.currentStock}</span>},
              {key:'reorder',label:'Reorder At',render:(r:any)=><span className="text-slate-400 text-xs">{r.reorderLevel||5}</span>},
              {key:'cost',label:'Unit Cost',render:(r:any)=><span className="font-semibold">{getCurrencySymbol()}{parseFloat(r.unitCost||0).toFixed(2)}</span>},
              {key:'gst',label:'VAT%',render:(r:any)=><span className="text-slate-400 text-xs">{r.gstPercentage||16}%</span>},
              {key:'rack',label:'Location',render:(r:any)=><span className="text-slate-400 text-xs">{r.rackLocation||r.location||'—'}</span>},
              {key:'actions',label:'',render:(r:any)=><button onClick={()=>openEditItem(r)} className="text-xs text-[#02357A] hover:underline">{t('common.edit','Edit')}</button>},
            ]}/>
          </div>
        </div>
      )}

      {/* STOCK */}
      {tab==='stock'&&(
        <div>
          <div className="mb-4"><Select onChange={e=>api.getStockSummary(e.target.value||undefined).then(r=>setStock(r.data||[]))} className="!w-44"><option value="">All Warehouses</option>{warehouses.map((w:any)=><option key={w.id} value={w.id}>{w.name}</option>)}</Select></div>
          <div className="card overflow-hidden">
            <DataTable loading={loading} emptyMessage="No stock data" emptyIcon="📊" data={stock} columns={[
              {key:'item',label:'Item',render:(r:any)=><div><p className="font-medium">{r.i_item_name}</p><p className="text-xs text-slate-400">{r.i_item_code}</p></div>},
              {key:'cat',label:'Category',render:(r:any)=><span className="text-slate-500 text-xs">{r.i_category||'—'}</span>},
              {key:'qty',label:'Total Qty',render:(r:any)=><span className={`font-bold ${+r.totalqty<=(+r.i_reorder_level||5)?'text-red-600':'text-green-600'}`}>{r.totalqty}</span>},
              {key:'rate',label:'Avg Rate',render:(r:any)=><span>{getCurrencySymbol()}{parseFloat(r.avgrate||0).toFixed(2)}</span>},
              {key:'value',label:'Value',render:(r:any)=><span className="font-semibold text-green-600">{getCurrencySymbol()}{parseFloat(r.totalvalue||0).toLocaleString('en-KE')}</span>},
            ]}/>
          </div>
        </div>
      )}

      {/* PURCHASE REQUISITIONS */}
      {tab==='pr'&&(
        <div className="card overflow-hidden">
          <DataTable loading={loading} emptyMessage="No requisitions" emptyIcon="📋" data={prs} columns={[
            {key:'prNumber',label:'PR #',render:(r:any)=><span className="font-mono text-xs">{r.prNumber}</span>},
            {key:'dept',label:'Department',render:(r:any)=><span>{r.department}</span>},
            {key:'priority',label:'Priority',render:(r:any)=><Badge label={r.priority} className={r.priority==='EMERGENCY'?'bg-red-100 text-red-700':r.priority==='URGENT'?'bg-amber-100 text-amber-700':'bg-slate-100 text-slate-600'}/>},
            {key:'date',label:'Date',render:(r:any)=><span className="text-xs text-slate-400">{fmtDate(r.requestDate)}</span>},
            {key:'status',label:'Status',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.status==='APPROVED'?'bg-green-100 text-green-700':r.status==='REJECTED'?'bg-red-100 text-red-700':r.status==='SUBMITTED'?'bg-blue-100 text-[#02357A]':'bg-slate-100 text-slate-500'}`}>{r.status}</span>},
            {key:'actions',label:'',render:(r:any)=>(
              <div className="flex gap-2">
                {r.status==='SUBMITTED'&&<><button onClick={()=>approvePR(r.id,true)} className="text-xs text-green-600 hover:underline">{t('common.approve','Approve')}</button><button onClick={()=>approvePR(r.id,false)} className="text-xs text-red-500 hover:underline">{t('common.reject','Reject')}</button></>}
                {r.status==='APPROVED'&&<button onClick={()=>{setPoForm({vendorId:'',vendorName:'',expectedDeliveryDate:'',notes:''});setShowPOForm(true);}} className="text-xs text-[#02357A] hover:underline">Create PO</button>}
              </div>
            )},
          ]}/>
        </div>
      )}

      {/* PURCHASE ORDERS */}
      {tab==='po'&&(
        <div className="card overflow-hidden">
          <DataTable loading={loading} emptyMessage="No purchase orders" emptyIcon="🛒" data={pos} columns={[
            {key:'poNumber',label:'PO #',render:(r:any)=><span className="font-mono text-xs">{r.poNumber}</span>},
            {key:'vendor',label:'Vendor',render:(r:any)=><p className="font-medium">{r.vendorName||'—'}</p>},
            {key:'date',label:'PO Date',render:(r:any)=><span className="text-xs text-slate-400">{fmtDate(r.poDate)}</span>},
            {key:'delivery',label:'Expected Delivery',render:(r:any)=><span className="text-xs">{r.expectedDeliveryDate?fmtDate(r.expectedDeliveryDate):'—'}</span>},
            {key:'net',label:'Net Amount',render:(r:any)=><span className="font-semibold">{getCurrencySymbol()}{parseFloat(r.net_amount||r.netAmount||0).toLocaleString('en-KE')}</span>},
            {key:'status',label:'Status',render:(r:any)=><Badge label={r.status} status={r.status}/>},
            {key:'actions',label:'',render:(r:any)=>r.status==='PENDING'&&<button onClick={()=>{setGrnForm({poNumber:r.poNumber,vendorInvoiceNumber:'',vendorInvoiceDate:'',warehouseId:'',remarks:''});setShowGRNForm(true);}} className="text-xs text-[#02357A] hover:underline">Receive (GRN)</button>},
          ]}/>
        </div>
      )}

      {/* GRN */}
      {tab==='grn'&&(
        <div className="card overflow-hidden">
          <DataTable loading={loading} emptyMessage="No GRNs" emptyIcon="📥" data={grns} columns={[
            {key:'grnNumber',label:'GRN #',render:(r:any)=><span className="font-mono text-xs">{r.grnNumber}</span>},
            {key:'po',label:'PO #',render:(r:any)=><span className="font-mono text-xs">{r.poNumber||'—'}</span>},
            {key:'invoice',label:'Vendor Invoice',render:(r:any)=><span className="text-xs">{r.vendorInvoiceNumber||'—'}</span>},
            {key:'date',label:'Receipt Date',render:(r:any)=><span className="text-xs text-slate-400">{fmtDateTime(r.receiptDate)}</span>},
            {key:'total',label:'Total',render:(r:any)=><span className="font-semibold">{getCurrencySymbol()}{parseFloat(r.total_amount||r.totalAmount||0).toLocaleString('en-KE')}</span>},
            {key:'status',label:'Status',render:(r:any)=><span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${r.status==='POSTED'?'bg-green-100 text-green-700':r.status==='VERIFIED'?'bg-blue-100 text-[#02357A]':'bg-amber-100 text-amber-700'}`}>{r.status}</span>},
            {key:'actions',label:'',render:(r:any)=>r.status!=='POSTED'&&<button onClick={()=>postGRN(r.id)} disabled={saving} className="text-xs text-green-600 hover:underline font-medium">Post (Update Stock)</button>},
          ]}/>
        </div>
      )}

      {/* DEPARTMENT ISSUES */}
      {tab==='issues'&&(
        <div className="card overflow-hidden">
          <DataTable loading={loading} emptyMessage="No issues" emptyIcon="🏥" data={issues} columns={[
            {key:'issueNumber',label:'Issue #',render:(r:any)=><span className="font-mono text-xs">{r.issue_number||r.issueNumber}</span>},
            {key:'to',label:'To Department',render:(r:any)=><span className="font-medium">{r.toDepartment}</span>},
            {key:'date',label:'Date',render:(r:any)=><span className="text-xs text-slate-400">{fmtDateTime(r.issue_date||r.issueDate)}</span>},
            {key:'status',label:'Status',render:(r:any)=><Badge label={r.status} status={r.status}/>},
          ]}/>
        </div>
      )}

      {/* EXPIRY */}
      {tab==='expiry'&&(
        <div>
          <div className="flex gap-2 mb-4 items-center"><p className="text-sm text-slate-500">Expiring within</p>
            {[30,60,90,180].map(d=><button key={d} onClick={()=>setExpiryDays(d)} className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all ${expiryDays===d?'bg-red-600 text-white border-red-600':'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>{d} days</button>)}
          </div>
          <div className="card overflow-hidden">
            <DataTable loading={loading} emptyMessage="No expiring stock" emptyIcon="✅" data={expiring} columns={[
              {key:'item',label:'Item',render:(r:any)=><span className="font-medium">{r.itemId}</span>},
              {key:'batch',label:'Batch',render:(r:any)=><span className="font-mono text-xs">{r.batchNumber||'—'}</span>},
              {key:'expiry',label:'Expiry',render:(r:any)=>{const days=Math.round((new Date(r.expiryDate).getTime()-Date.now())/86400000);return<div><p className="font-medium">{fmtDate(r.expiryDate)}</p><p className={`text-xs font-bold ${days<0?'text-red-700':days<30?'text-red-600':days<60?'text-amber-600':'text-orange-500'}`}>{days<0?`EXPIRED ${Math.abs(days)}d ago`:`${days} days left`}</p></div>;}},
              {key:'qty',label:'Current Qty',render:(r:any)=><span className="font-bold">{r.currentQuantity}</span>},
              {key:'rate',label:'Purchase Rate',render:(r:any)=><span>{getCurrencySymbol()}{parseFloat(r.purchaseRate||0).toFixed(2)}</span>},
              {key:'value',label:'Value',render:(r:any)=><span className="font-semibold text-amber-600">{getCurrencySymbol()}{(+r.currentQuantity*(+r.purchaseRate||0)).toLocaleString('en-KE')}</span>},
            ]}/>
          </div>
        </div>
      )}

      {/* REPORTS */}
      {tab==='reports'&&(
        <div className="space-y-5">
          <div className="card p-4 flex gap-3 items-end flex-wrap">
            <div><label className="label">From</label><Input type="date" value={reportDates.from} onChange={e=>setRD({...reportDates,from:e.target.value})}/></div>
            <div><label className="label">To</label><Input type="date" value={reportDates.to} onChange={e=>setRD({...reportDates,to:e.target.value})}/></div>
            <button onClick={loadTab} className="btn-primary text-sm mb-0.5">{t('common.apply','Apply')}</button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="card overflow-hidden">
              <div className="p-4 border-b"><h3 className="font-semibold text-slate-700">Department-wise Consumption</h3></div>
              <DataTable loading={loading} emptyMessage="No data" emptyIcon="📊" data={reportData||[]} columns={[
                {key:'dept',label:'Department',render:(r:any)=><span className="font-medium">{r.department}</span>},
                {key:'qty',label:'Total Issued',render:(r:any)=><span className="font-semibold">{r.total_issued}</span>},
                {key:'cost',label:'Total Cost',render:(r:any)=><span className="font-semibold text-red-600">{getCurrencySymbol()}{parseFloat(r.total_cost||0).toLocaleString('en-KE')}</span>},
              ]}/>
            </div>
            <div className="card overflow-hidden">
              <div className="p-4 border-b"><h3 className="font-semibold text-slate-700">Inventory Valuation</h3></div>
              <DataTable loading={loading} emptyMessage="No data" emptyIcon="💰" data={valuationData.slice(0,20)} columns={[
                {key:'item',label:'Item',render:(r:any)=><div><p className="font-medium">{r.i_item_name}</p><p className="text-xs text-slate-400">{r.i_category}</p></div>},
                {key:'qty',label:'Qty',render:(r:any)=><span>{Math.round(+r.totalqty||0)}</span>},
                {key:'value',label:'Value',render:(r:any)=><span className="font-semibold text-green-600">{getCurrencySymbol()}{parseFloat(r.totalvalue||0).toLocaleString('en-KE')}</span>},
              ]}/>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODALS ─── */}

      {/* Item Form */}
      <SlidePanel isOpen={showItemForm} onClose={()=>setShowItemForm(false)} title={editItemId?'Edit Item':'Add Item to Master'} size="lg">
        <form onSubmit={saveItem} className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="label">Item Name *</label><Input {...ifi('itemName')} required/></div>
          <div><label className="label">Item Code</label><Input {...ifi('itemCode')} placeholder="Auto-generated if blank"/></div>
          <div><label className="label">Item Type</label><Select {...ifi('itemType')}>{ITEM_TYPES.map(t=><option key={t}>{t}</option>)}</Select></div>
          <div><label className="label">{t('common.category','Category')}</label><Select {...ifi('category')}><option value="">{t('common.select','Select')}</option>{CATEGORIES.map(c=><option key={c}>{c}</option>)}</Select></div>
          <div><label className="label">Sub-Category</label><Input {...ifi('subCategory')} placeholder="Optional sub-category"/></div>
          <div><label className="label">Unit of Measure</label><Select {...ifi('unit')}>{['PCS','BOX','VIAL','ML','STRIP','PACKET','ROLL','KG','LITRE','PAIR'].map(u=><option key={u}>{u}</option>)}</Select></div>
          <div><label className="label">HSN Code</label><Input {...ifi('hsnCode')} placeholder="6-digit HSN"/></div>
          <div><label className="label">VAT %</label><Input type="number" {...ifi('gstPercentage')}/></div>
          <div><label className="label">Unit Cost ({getCurrencySymbol()})</label><Input type="number" {...ifi('unitCost')}/></div>
          <div><label className="label">Reorder Level</label><Input type="number" {...ifi('reorderLevel')}/></div>
          <div><label className="label">Reorder Quantity</label><Input type="number" {...ifi('reorderQuantity')}/></div>
          <div><label className="label">Rack/Location</label><Input {...ifi('rackLocation')} placeholder="e.g. A-12, Rack 3"/></div>
          <div className="col-span-2 flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Saving...':editItemId?'Update Item':'Add Item'}</button><button type="button" onClick={()=>setShowItemForm(false)} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </form>
      </SlidePanel>

      {/* PR Form */}
      <SlidePanel isOpen={showPRForm} onClose={()=>setShowPRForm(false)} title="New Purchase Requisition" size="lg">
        <form onSubmit={submitPR} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="label">{t('common.department','Department')}</label><Select value={prForm.department} onChange={e=>setPRForm({...prForm,department:e.target.value})}>{DEPARTMENTS.map(d=><option key={d}>{d}</option>)}</Select></div>
            <div><label className="label">Priority</label><Select value={prForm.priority} onChange={e=>setPRForm({...prForm,priority:e.target.value})}>{PR_PRIORITIES.map(p=><option key={p}>{p}</option>)}</Select></div>
            <div><label className="label">Expected Date</label><Input type="date" value={prForm.expectedDate} onChange={e=>setPRForm({...prForm,expectedDate:e.target.value})}/></div>
          </div>
          <div>
            <p className="label mb-2">Items Required</p>
            {prItems.map((it,i)=>(
              <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-end">
                <div className="col-span-2"><Input placeholder="Item name" value={it.itemName} onChange={e=>setPRItems(prItems.map((x,xi)=>xi===i?{...x,itemName:e.target.value}:x))}/></div>
                <div><Input type="number" placeholder="Qty" value={it.quantityRequested} onChange={e=>setPRItems(prItems.map((x,xi)=>xi===i?{...x,quantityRequested:e.target.value}:x))}/></div>
                <div><Select value={it.unit} onChange={e=>setPRItems(prItems.map((x,xi)=>xi===i?{...x,unit:e.target.value}:x))}>{['PCS','BOX','VIAL','ML','STRIP','PACKET'].map(u=><option key={u}>{u}</option>)}</Select></div>
                <button type="button" onClick={()=>setPRItems(prItems.filter((_,xi)=>xi!==i))} className="text-red-400 hover:text-red-600 h-9">✕</button>
              </div>
            ))}
            <button type="button" onClick={()=>setPRItems([...prItems,{itemId:'',itemName:'',quantityRequested:'',unit:'PCS',lastPrice:''}])} className="btn-secondary text-xs">+ Add Item</button>
          </div>
          <div><label className="label">{t('common.remarks','Remarks')}</label><Input value={prForm.remarks} onChange={e=>setPRForm({...prForm,remarks:e.target.value})}/></div>
          <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Creating...':'Submit PR'}</button><button type="button" onClick={()=>setShowPRForm(false)} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </form>
      </SlidePanel>

      {/* PO Form */}
      <SlidePanel isOpen={showPOForm} onClose={()=>setShowPOForm(false)} title="Create Purchase Order" size="xl">
        <form onSubmit={submitPO} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="label">Vendor *</label><Select value={poForm.vendorId} onChange={e=>{const v=vendors.find((x:any)=>x.id===e.target.value);setPoForm({...poForm,vendorId:e.target.value,vendorName:v?.vendorName||''});}} required><option value="">Select vendor</option>{vendors.map((v:any)=><option key={v.id} value={v.id}>{v.vendorName}</option>)}</Select></div>
            <div><label className="label">Expected Delivery</label><Input type="date" value={poForm.expectedDeliveryDate} onChange={e=>setPoForm({...poForm,expectedDeliveryDate:e.target.value})}/></div>
          </div>
          <div>
            <p className="label mb-2">PO Items</p>
            {poItems.map((it,i)=>(
              <div key={i} className="grid grid-cols-6 gap-2 mb-2 items-end">
                <div className="col-span-2"><Input placeholder="Item name" value={it.itemName} onChange={e=>setPoItems(poItems.map((x,xi)=>xi===i?{...x,itemName:e.target.value}:x))}/></div>
                <div><Input type="number" placeholder="Qty" value={it.quantity} onChange={e=>setPoItems(poItems.map((x,xi)=>xi===i?{...x,quantity:e.target.value}:x))}/></div>
                <div><Input type="number" placeholder={`Unit Price ${getCurrencySymbol()}`} value={it.unitPrice} onChange={e=>setPoItems(poItems.map((x,xi)=>xi===i?{...x,unitPrice:e.target.value}:x))}/></div>
                <div><Input type="number" placeholder="VAT %" value={it.taxPercentage} onChange={e=>setPoItems(poItems.map((x,xi)=>xi===i?{...x,taxPercentage:e.target.value}:x))}/></div>
                <button type="button" onClick={()=>setPoItems(poItems.filter((_,xi)=>xi!==i))} className="text-red-400 h-9">✕</button>
              </div>
            ))}
            <button type="button" onClick={()=>setPoItems([...poItems,{itemId:'',itemName:'',quantity:'',unitPrice:'',taxPercentage:'12',unit:'PCS'}])} className="btn-secondary text-xs mt-1">+ Add Item</button>
          </div>
          <div><label className="label">{t('common.notes','Notes')}</label><Input value={poForm.notes} onChange={e=>setPoForm({...poForm,notes:e.target.value})}/></div>
          <div className="flex gap-3"><button type="submit" disabled={saving||!poForm.vendorId} className="btn-primary flex-1">{saving?'Creating...':'Create PO'}</button><button type="button" onClick={()=>setShowPOForm(false)} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </form>
      </SlidePanel>

      {/* GRN Form */}
      <SlidePanel isOpen={showGRNForm} onClose={()=>setShowGRNForm(false)} title="New Goods Receipt Note (GRN)" size="xl">
        <form onSubmit={submitGRN} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="label">PO Number (if any)</label><Input value={grnForm.poNumber} onChange={e=>setGrnForm({...grnForm,poNumber:e.target.value})}/></div>
            <div><label className="label">Vendor Invoice #</label><Input value={grnForm.vendorInvoiceNumber} onChange={e=>setGrnForm({...grnForm,vendorInvoiceNumber:e.target.value})}/></div>
            <div><label className="label">Warehouse</label><Select value={grnForm.warehouseId} onChange={e=>setGrnForm({...grnForm,warehouseId:e.target.value})}><option value="">{t('common.select','Select')}</option>{warehouses.map((w:any)=><option key={w.id} value={w.id}>{w.name}</option>)}</Select></div>
            <div><label className="label">Invoice Date</label><Input type="date" value={grnForm.vendorInvoiceDate} onChange={e=>setGrnForm({...grnForm,vendorInvoiceDate:e.target.value})}/></div>
          </div>
          <div>
            <div className="grid grid-cols-7 gap-1.5 text-xs font-semibold text-slate-500 mb-1 px-1"><span className="col-span-2">Item</span><span>Batch #</span><span>Expiry</span><span>Qty</span><span>Rate {getCurrencySymbol()}</span><span>MRP {getCurrencySymbol()}</span></div>
            {grnItems.map((it,i)=>(
              <div key={i} className="grid grid-cols-7 gap-1.5 mb-2 items-end">
                <div className="col-span-2"><Input placeholder="Item name" value={it.itemName} onChange={e=>setGrnItems(grnItems.map((x,xi)=>xi===i?{...x,itemName:e.target.value}:x))}/></div>
                <div><Input placeholder="Batch" value={it.batchNumber} onChange={e=>setGrnItems(grnItems.map((x,xi)=>xi===i?{...x,batchNumber:e.target.value}:x))}/></div>
                <div><Input type="date" value={it.expiryDate} onChange={e=>setGrnItems(grnItems.map((x,xi)=>xi===i?{...x,expiryDate:e.target.value}:x))}/></div>
                <div><Input type="number" placeholder="Qty" value={it.quantityReceived} onChange={e=>setGrnItems(grnItems.map((x,xi)=>xi===i?{...x,quantityReceived:e.target.value}:x))}/></div>
                <div><Input type="number" placeholder="Rate" value={it.purchaseRate} onChange={e=>setGrnItems(grnItems.map((x,xi)=>xi===i?{...x,purchaseRate:e.target.value}:x))}/></div>
                <div className="flex gap-1"><Input type="number" placeholder="MRP" value={it.saleRate} onChange={e=>setGrnItems(grnItems.map((x,xi)=>xi===i?{...x,saleRate:e.target.value}:x))}/><button type="button" onClick={()=>setGrnItems(grnItems.filter((_,xi)=>xi!==i))} className="text-red-400 h-9 px-1">✕</button></div>
              </div>
            ))}
            <button type="button" onClick={()=>setGrnItems([...grnItems,{itemId:'',itemName:'',batchNumber:'',expiryDate:'',quantityReceived:'',purchaseRate:'',saleRate:''}])} className="btn-secondary text-xs mt-1">+ Add Item</button>
          </div>
          <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Creating...':'Create GRN'}</button><button type="button" onClick={()=>setShowGRNForm(false)} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </form>
      </SlidePanel>

      {/* Issue Form */}
      <SlidePanel isOpen={showIssueForm} onClose={()=>setShowIssueForm(false)} title="Issue Items to Department" size="lg">
        <form onSubmit={submitIssue} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">From Warehouse</label><Select value={issueForm.fromWarehouseId} onChange={e=>setIssueForm({...issueForm,fromWarehouseId:e.target.value})}><option value="">{t('common.select','Select')}</option>{warehouses.map((w:any)=><option key={w.id} value={w.id}>{w.name}</option>)}</Select></div>
            <div><label className="label">To Department *</label><Select value={issueForm.toDepartment} onChange={e=>setIssueForm({...issueForm,toDepartment:e.target.value})} required>{DEPARTMENTS.map(d=><option key={d}>{d}</option>)}</Select></div>
          </div>
          <div>
            <p className="label mb-2">Items to Issue</p>
            {issueItems.map((it,i)=>(
              <div key={i} className="grid grid-cols-5 gap-2 mb-2 items-end">
                <div className="col-span-2"><Input placeholder="Item name" value={it.itemName} onChange={e=>setIssueItems(issueItems.map((x,xi)=>xi===i?{...x,itemName:e.target.value}:x))}/></div>
                <div><Input placeholder="Batch (opt)" value={it.batchNumber} onChange={e=>setIssueItems(issueItems.map((x,xi)=>xi===i?{...x,batchNumber:e.target.value}:x))}/></div>
                <div><Input type="number" placeholder="Qty" value={it.quantityIssued} onChange={e=>setIssueItems(issueItems.map((x,xi)=>xi===i?{...x,quantityIssued:e.target.value}:x))}/></div>
                <button type="button" onClick={()=>setIssueItems(issueItems.filter((_,xi)=>xi!==i))} className="text-red-400 h-9">✕</button>
              </div>
            ))}
            <button type="button" onClick={()=>setIssueItems([...issueItems,{itemId:'',itemName:'',batchNumber:'',quantityRequested:'',quantityIssued:'',unitCost:''}])} className="btn-secondary text-xs mt-1">+ Add Item</button>
          </div>
          <div><label className="label">{t('common.remarks','Remarks')}</label><Input value={issueForm.remarks} onChange={e=>setIssueForm({...issueForm,remarks:e.target.value})}/></div>
          <div className="flex gap-3"><button type="submit" disabled={saving} className="btn-primary flex-1">{saving?'Issuing...':'Issue Items'}</button><button type="button" onClick={()=>setShowIssueForm(false)} className="btn-secondary">{t('common.cancel','Cancel')}</button></div>
        </form>
      </SlidePanel>
    </Layout>
  );
}
